import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useHistory } from 'react-router-dom';
import { Drawer } from 'antd';
import { useAuth } from '@clerk/clerk-react';
import {
  AppstoreOutlined,
  SearchOutlined,
  UserOutlined,
  StarOutlined,
  LockOutlined,
  DatabaseOutlined,
  QuestionCircleOutlined,
  UnorderedListOutlined,
  LogoutOutlined,
} from '@ant-design/icons';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

const MobileSideMenu = ({ isOpen, onClose }) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const [isSubscriptionInactive, setIsSubscriptionInactive] = useState(false);

  // Fetch subscription status
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!isLoaded || !isSignedIn) return;
      
      try {
        const token = await getToken({ template: "default" });
        const response = await fetch(`${BASE_URL}/user/details`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.ok) {
          const userDetails = await response.json();
          const isInactive = userDetails.subscription_status === 'INACTIVE' || userDetails.subscription_status === 'inactive';
          setIsSubscriptionInactive(isInactive);
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
      }
    };

    checkSubscriptionStatus();
  }, [isLoaded, isSignedIn, getToken]);

  const navItems = [
    { 
      key: 'dashboard', 
      label: 'Home', 
      icon: <AppstoreOutlined />, 
      path: '/dashboard' 
    },
    { 
      key: 'search-insights', 
      label: 'Market Search', 
      icon: <SearchOutlined />, 
      path: '/search' 
    },
    { 
      key: 'buy-signals', 
      label: 'Buy Signals', 
      icon: <StarOutlined />, 
      path: '/buy-signals' 
    },
    { 
      key: 'dealer-insights', 
      label: 'Lot Performance', 
      icon: <DatabaseOutlined />, 
      path: '/dealer-insights' 
    },
    { 
      key: 'account', 
      label: 'Account', 
      icon: <UserOutlined />, 
      path: '/profile' 
    },
    { 
      key: 'support', 
      label: 'Support', 
      icon: <QuestionCircleOutlined />, 
      path: '/support'
    },
    { 
      key: 'logout', 
      label: 'Logout', 
      icon: <LogoutOutlined />, 
      path: null,
      isLogout: true
    },
  ];

  const isActive = (itemPath, itemKey) => {
    const currentPath = pathname;
    return currentPath === itemPath || 
           currentPath.startsWith(itemPath + '/') ||
           (itemKey !== 'dashboard' && currentPath.includes(itemKey));
  };

  // Restricted items when subscription is inactive (Account, Billing, Support remain allowed)
  const restrictedItems = ['search-insights', 'buy-signals', 'recommendations', 'private-listings', 'dealer-insights'];
  const isItemDisabled = (itemKey) => isSubscriptionInactive && restrictedItems.includes(itemKey);

  const handleNavClick = (item) => {
    // Prevent navigation if item is disabled
    if (isItemDisabled(item.key)) {
      return;
    }

    // Close the drawer first
    onClose();
    
    // Handle logout
    if (item.isLogout) {
      if (window.Clerk?.signOut) {
        window.Clerk.signOut();
      }
      history.push('/sign-in');
      return;
    }
    
    // Handle external links (like mailto)
    if (item.isExternal && item.externalUrl) {
      window.location.href = item.externalUrl;
      return;
    }
    
    // Navigate immediately
    history.push(item.path);
  };

  return (
    <Drawer
      title={false}
      placement="left"
      closable={false}
      onClose={onClose}
      open={isOpen}
      width="100%"
      className="mobile-side-menu-drawer"
      styles={{
        body: {
          padding: 0,
          background: '#0d1117',
          width: '100vw',
          height: '100vh',
        },
        header: {
          display: 'none',
        },
      }}
      maskStyle={{
        background: 'rgba(0, 0, 0, 0.45)',
      }}
      getContainer={false}
    >
      <div className="mobile-side-menu">
        {/* Header with brand and close button */}
        <div className="mobile-menu-header">
          <div className="mobile-menu-brand">
            <h2 className="mobile-menu-title">Hooptie</h2>
          </div>
          <button className="mobile-menu-close" onClick={onClose}>
            <span>×</span>
          </button>
        </div>

        {/* Navigation items */}
        <nav className="mobile-menu-nav">
          {navItems.map((item) => {
            const isDisabled = isItemDisabled(item.key);
            const isLogout = item.isLogout;
            return (
              <div
                key={item.key}
                className={`mobile-nav-item ${isActive(item.path, item.key) ? 'active' : ''} ${isDisabled ? 'mobile-nav-item-disabled' : ''} ${isLogout ? 'mobile-nav-item-logout' : ''}`}
                onClick={() => handleNavClick(item)}
                style={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                  pointerEvents: isDisabled ? 'none' : 'auto'
                }}
              >
                <span className="mobile-nav-icon">{item.icon}</span>
                <span className="mobile-nav-label">{item.label}</span>
              </div>
            );
          })}
        </nav>
      </div>
    </Drawer>
  );
};

export default MobileSideMenu;
